'use client';

import { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface JurisdictionResult {
  source: 'fresh' | 'cache';
  geocode: {
    formatted_address: string;
    street: string;
    city: string;
    county: string;
    state: string;
    postal_code: string;
    lat: number;
    lng: number;
  } | null;
  jurisdiction_key: string;
  playbook: {
    id: string;
    jurisdiction_key: string;
    jurisdiction_name: string;
    jurisdiction_type: string;
    state: string;
    county: string;
    city: string | null;
    permit_portal_url: string | null;
    permit_portal_phone: string | null;
    fee_formula_text: string | null;
    required_documents: string[];
    turnaround_days_typical: number | null;
    contractor_registration_required: boolean;
    contractor_registration_notes: string | null;
    inspections_required: Array<{ type: string; required: boolean; notes?: string }>;
    special_notes: string | null;
    source_urls: string[];
    confidence_score: number;
    last_refreshed_at: string;
  };
}

interface IdentifyJurisdictionButtonProps {
  address: string;
  disabled?: boolean;
  label?: string;
  variant?: 'primary' | 'compact';
  onSuccess?: (result: JurisdictionResult) => void;
  onError?: (error: string) => void;
}

const IdentifyJurisdictionButton = ({
  address,
  disabled,
  label,
  variant = 'primary',
  onSuccess,
  onError,
}: IdentifyJurisdictionButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  const handleClick = async () => {
    if (!address || address.trim().length < 10) {
      toast.error('Address is too short to identify.');
      onError?.('Invalid address');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/permits/identify-jurisdiction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: address.trim(),
          user_id: currentUser?.id ?? null,
          user_name: currentUser
            ? `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim()
            : null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = data?.error ?? `Request failed: ${response.status}`;
        toast.error(msg, { duration: 5000 });
        onError?.(msg);
        return;
      }
      toast.success(
        data.source === 'cache'
          ? `Cached playbook loaded: ${data.playbook?.jurisdiction_name ?? '(unknown)'}`
          : `AI identified: ${data.playbook?.jurisdiction_name ?? '(unknown)'} (confidence ${Math.round((data.playbook?.confidence_score ?? 0) * 100)}%)`,
        { duration: 4000, icon: data.source === 'cache' ? '⚡' : '🤖' }
      );
      onSuccess?.(data as JurisdictionResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`AI lookup failed: ${msg}`, { duration: 6000 });
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: loading ? '#ede9fe' : 'rgba(124, 58, 237, 0.1)',
          color: '#7c3aed',
        }}
      >
        {loading ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : (
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinejoin="round" />
          </svg>
        )}
        <span>{label ?? 'Re-identify with AI'}</span>
      </button>
    );
  }

  // Primary variant
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        background: loading
          ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
          : 'linear-gradient(135deg, #E89A3C 0%, #7c3aed 100%)',
        boxShadow: loading ? 'none' : '0 4px 12px rgba(124, 58, 237, 0.25)',
      }}
    >
      {loading ? (
        <>
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span>Looking up jurisdiction…</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M12 2L2 7l10 5 10-5-10-5z" strokeLinejoin="round" />
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" strokeLinejoin="round" />
          </svg>
          <span>{label ?? 'Identify Jurisdiction with AI'}</span>
        </>
      )}
    </button>
  );
};

export default IdentifyJurisdictionButton;
